import React, { useCallback, useRef, useState } from 'react';
import ConfidentialDownloadDialog from '../components/provider/ConfidentialDownloadDialog';
import { useAuth } from '../../hooks/useAuth';
import { apiRecordConfidentialWarningSeen } from '../utils/rmsApi';
import { confidentialWarningSeenBy } from '../utils/confidentialWarning';

/**
 * useConfidentialDownload
 * -----------------------
 * The confidentiality confirm every triage-application download passes
 * through (TDI-484): call `requestDownload(fn)` instead of `fn()`, render the
 * returned `confirmDialog` once in the host, and `fn` runs only when the
 * user chooses Continue. Cancel (or closing the dialog) runs nothing.
 *
 * Once per user per patient (TDI-609): the warning shows on a user's first
 * download for a patient record and never again for that record. Continue
 * records the acknowledgement on the record through a callable
 * (confidentialWarningSeen.{uid}), so it holds after a reload and on another
 * device; the host passes the live record in and the flag is read from it.
 * Until the record says so, the acknowledgement made here is remembered by
 * this instance, so a second download straight after the first does not ask
 * again while the record catches up. Without a record or a signed-in user
 * the warning shows every time, as before: the safe default.
 *
 * Viewing never comes through here, so View never shows the warning.
 *
 * @param {{ orgId?: string, patientRecordId?: string, patientRecord?: object|null }} [options]
 * @returns {{ requestDownload: (fn: Function) => void, confirmDialog: React.ReactElement }}
 */
export default function useConfidentialDownload({ orgId, patientRecordId, patientRecord } = {}) {
  const { user } = useAuth() || {};
  const uid = user?.uid || null;
  const [open, setOpen] = useState(false);
  const pendingFn = useRef(null);
  // The record ids this user acknowledged here, before the record shows it.
  const acknowledgedHere = useRef(new Set());

  const alreadySeen = Boolean(
    uid && patientRecordId && (
      confidentialWarningSeenBy(patientRecord, uid)
      || acknowledgedHere.current.has(`${uid}:${patientRecordId}`)
    ),
  );

  const requestDownload = useCallback((fn) => {
    if (alreadySeen) {
      if (fn) fn();
      return;
    }
    pendingFn.current = fn;
    setOpen(true);
  }, [alreadySeen]);

  const handleConfirm = useCallback(() => {
    const fn = pendingFn.current;
    pendingFn.current = null;
    setOpen(false);
    if (uid && orgId && patientRecordId) {
      acknowledgedHere.current.add(`${uid}:${patientRecordId}`);
      // A failed record only means the warning shows once more next time;
      // it never costs the user the download they confirmed.
      apiRecordConfidentialWarningSeen({ orgId, patientRecordId }).catch((err) => {
        console.error('Could not record the confidentiality acknowledgement:', err);
      });
    }
    if (fn) fn();
  }, [uid, orgId, patientRecordId]);

  const handleOpenChange = useCallback((next) => {
    if (!next) pendingFn.current = null;
    setOpen(next);
  }, []);

  const confirmDialog = (
    <ConfidentialDownloadDialog
      open={open}
      onOpenChange={handleOpenChange}
      onConfirm={handleConfirm}
    />
  );

  return { requestDownload, confirmDialog };
}
